import { createDepotConfiguration, createConnection, createServer } from '../TransportUtils';
import { ReportDataLevel, Connection, Depot, UserInfo, Capability } from '@sas-dvr/ltjs-transport';
import { ltjsProperty, ltjsDestructor } from '../../ltjs-decorators';
import { whenInitialized } from '@sas-dvr/ltjs-commons/ltjsEnvironment';
import { IServer } from './IServer';
import {
  ConnectionStatus,
  ServerType,
} from '@sas-dvr/internal-va-react-core/store/transport/transportState';
import { ReplaySubject, Observable, Subscription, combineLatest, asyncScheduler } from 'rxjs';
import { IReport } from './IReport';
import { LocalReport } from './LocalReport';
import {
  hasHighContrastReportTheme$,
  getUseHighContrastReportTheme,
} from '@sas-dvr/internal-va-react-core/config';
import { locale$, formatterLocale$ } from '@sas-dvr/internal-va-react-core/config';
import { isUrlCrossDomain } from '@sas-dvr/ltjs-commons/ltjsUtil';
import { debounceTime, distinctUntilChanged, skip, takeUntil } from 'rxjs/operators';

export class LocalServer implements IServer {
  @ltjsProperty
  private _depot: Depot | undefined;
  private _depotSubscription: Subscription | undefined;

  @ltjsProperty
  private _connection: Connection | null = null;

  private _url: string;
  private _errorNotifier = new ReplaySubject<string | undefined>(1);
  private _currentError: string | undefined;
  private _statusNotifier = new ReplaySubject<ConnectionStatus>(1);
  private _currentStatus: ConnectionStatus = 'initalizing';
  private _report: LocalReport | undefined;
  private _iframeSandbox: string | undefined;
  private _destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  getUrl() {
    return this._url;
  }

  getServerType(): ServerType {
    return 'standalone';
  }

  setIframeSandbox(value: string) {
    this._iframeSandbox = value;
  }

  getIframeSandbox() {
    return this._iframeSandbox;
  }

  constructor(url: string) {
    this._url = url;
    whenInitialized().then(() => {
      this._depot = new Depot(
        createDepotConfiguration({
          dataLevel: ReportDataLevel.OFFLINE,
          highContrast: getUseHighContrastReportTheme(),
        })
      );
      this._depotSubscription = hasHighContrastReportTheme$.subscribe((value) => {
        if (this._depot?.getDepotConfiguration()?.getUseHighContrastReportStyle() !== value) {
          this._depot?.setUseHighContrastReportStyle(value);
          this._depot?.removeAllReports();
          this._report?.reload();
        }
      });

      combineLatest([locale$, formatterLocale$])
        .pipe(
          takeUntil(this._destroyed$),
          distinctUntilChanged((a, b) => a[0] === b[0] && a[1] === b[1]),
          skip(1),
          debounceTime(0, asyncScheduler)
        )
        .subscribe(() => {
          this._report?.reload();
        });

      this._connection = createConnection(
        createServer('http://__standalone_location_token__/', 850)
      );
      // HACK: Some maps request go through commons_resources_SASNetworkManagerImplementation_processNetworkRequest
      //  which forces credentials to be 'include' or 'omit'. However, we want to use the browser's default
      //  which only incudes credentials for same-origin requests.
      if (isUrlCrossDomain(url)) {
        this._connection.setUseGuestCredentials(true);
      }
      this._connection.setUserId('000');
      const info = new UserInfo();
      info.setCapabilities([Capability.ALLOW_WEB_CONTENT]);
      this._connection.setUserInfo(info);
      const warning = this._depot.addConnection(this._connection);
      if (warning) {
        console.error(warning.getMessage());
      }
      this._currentStatus = 'connected';
      this._statusNotifier.next('connected');
    });
  }

  /**
   * Stream for notifying ConnectionStatus changes. Will replay the latest status when subscribed.
   */
  getStatusNotifier(): Observable<ConnectionStatus> {
    return this._statusNotifier;
  }

  /**
   * Stream for notifying changes to the error state of SASServer. Will replay the latest error state when subscribed.
   */
  getErrorNotifier(): Observable<string | undefined> {
    return this._errorNotifier;
  }

  getError() {
    return this._currentError;
  }

  getStatus() {
    return this._currentStatus;
  }

  /**
   * @internal
   */
  getConnection() {
    return this._connection;
  }

  /**
   * @internal
   */
  getReportManager() {
    return this._depot!.getReportManager(this._connection!);
  }

  /**
   * @internal
   */
  getConnectionManager() {
    return this._depot!.getConnectionManager(this._connection!);
  }

  createReport(): IReport {
    if (this._report) {
      return this._report;
    }
    this._report = new LocalReport(this);
    return this._report;
  }
  get isConnectionEstablished() {
    return true;
  }

  removeReport() {
    this._report = undefined;
  }

  @ltjsDestructor
  close() {
    this._depotSubscription?.unsubscribe();
    this._depot?.shutdown();
    this._report?.close();
    this._destroyed$.next(true);
  }
}
